import { useEffect, useState } from 'react';
import { serverNow } from '../../time-sync';
import type { GameSnapshot } from '../../socket';

// Stadium-style football board: big score columns flanking a center
// stack with clock, period, down/distance and ball spot. Keeps the
// look spec-aligned but sport-specific.

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function useLiveClock(
  m: number,
  s: number,
  running: boolean,
  startedAt: string | null | undefined,
): { m: number; s: number } {
  const [, force] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running]);
  if (!running || !startedAt) return { m, s };
  const elapsed = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  const total = Math.max(0, m * 60 + s - elapsed);
  return { m: Math.floor(total / 60), s: total % 60 };
}

function useLivePlayClock(
  value: number,
  running: boolean,
  startedAt: string | null | undefined,
): number {
  const [, force] = useState(0);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [running]);
  if (!running || !startedAt) return value;
  const elapsed = Math.max(
    0,
    Math.floor((serverNow() - new Date(startedAt).getTime()) / 1000),
  );
  return Math.max(0, value - elapsed);
}

export function FootballScoreboard({
  snapshot,
  isSample,
  connected,
}: {
  snapshot: GameSnapshot | null;
  isSample: boolean;
  connected: boolean;
}) {
  const s = snapshot?.state;
  const home = snapshot?.rosters.home;
  const away = snapshot?.rosters.away;

  const homeScore = s?.home.score ?? (isSample ? 21 : 0);
  const awayScore = s?.away.score ?? (isSample ? 14 : 0);
  const period = s?.period ?? (isSample ? 3 : 1);

  const clock = useLiveClock(
    s?.clock.m ?? 12,
    s?.clock.s ?? 30,
    s?.clock.running ?? false,
    s?.clock.startedAt ?? null,
  );

  const ss = (s?.sportState ?? {}) as Record<string, unknown>;
  const playClockRaw = typeof ss.playClock === 'number' ? (ss.playClock as number) : 40;
  const playClockRunning = ss.playClockRunning === true;
  const playClockStartedAt =
    typeof ss.playClockStartedAt === 'string' ? (ss.playClockStartedAt as string) : null;
  const playClock = useLivePlayClock(playClockRaw, playClockRunning, playClockStartedAt);

  const down = typeof ss.down === 'number' ? (ss.down as number) : 1;
  const distance = typeof ss.distance === 'number' ? (ss.distance as number) : 10;
  const yardLine = typeof ss.yardLine === 'number' ? (ss.yardLine as number) : 25;
  const yardSide = ss.yardSide === 'home' || ss.yardSide === 'away' ? (ss.yardSide as 'home' | 'away') : 'home';

  const downSuf = down === 1 ? 'ST' : down === 2 ? 'ND' : down === 3 ? 'RD' : 'TH';
  const homeColor = home?.colorPrimary ?? '#dc2626';
  const awayColor = away?.colorPrimary ?? '#1d4ed8';
  const homeName = home?.name ?? 'HOME';
  const awayName = away?.name ?? 'AWAY';

  const possession = s?.possession;

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black text-white">
      {!connected && !isSample && (
        <div className="absolute right-6 top-6 z-10 rounded-full bg-red-600/90 px-3 py-1 text-xs font-bold uppercase">
          Offline
        </div>
      )}

      <div className="grid h-full grid-cols-[1fr_auto_1fr]">
        {/* HOME side */}
        <div
          className="flex flex-col items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${homeColor}, #000)` }}
        >
          <div className="text-2xl font-bold uppercase tracking-[0.4em] opacity-80">
            {homeName}
          </div>
          <div className="my-4 font-mono text-[18rem] font-black leading-none">
            {homeScore}
          </div>
          {possession === 'home' && (
            <div className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              ◀ Possession
            </div>
          )}
        </div>

        {/* CENTER stack */}
        <div className="flex w-[28rem] flex-col items-center justify-center gap-6 bg-zinc-950 px-8 py-12">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.4em] text-white/40">
              Quarter
            </div>
            <div className="mt-1 font-mono text-7xl font-bold">{period}</div>
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.4em] text-white/40">
              Game clock
            </div>
            <div className="mt-1 font-mono text-7xl font-bold tabular-nums">
              {clock.m}:{pad(clock.s)}
            </div>
          </div>
          <div className="grid w-full grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-white/40">
                Down
              </div>
              <div className="mt-0.5 font-mono text-3xl font-bold">
                {down}
                <span className="ml-0.5 text-base text-white/50">{downSuf}</span>
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-white/40">
                To go
              </div>
              <div className="mt-0.5 font-mono text-3xl font-bold">
                {distance}
              </div>
            </div>
          </div>
          <div className="w-full rounded-lg border border-white/10 bg-white/5 p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-white/40">
              Ball on
            </div>
            <div className="mt-0.5 font-mono text-3xl font-bold">
              {yardSide.toUpperCase()} {yardLine}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-white/40">
              Play clock
            </div>
            <div
              className={`mt-0.5 font-mono text-5xl font-bold tabular-nums ${
                playClock <= 5 ? 'text-red-400' : playClockRunning ? 'text-amber-300' : ''
              }`}
            >
              {playClock}
            </div>
          </div>
        </div>

        {/* AWAY side */}
        <div
          className="flex flex-col items-center justify-center"
          style={{ background: `linear-gradient(225deg, ${awayColor}, #000)` }}
        >
          <div className="text-2xl font-bold uppercase tracking-[0.4em] opacity-80">
            {awayName}
          </div>
          <div className="my-4 font-mono text-[18rem] font-black leading-none">
            {awayScore}
          </div>
          {possession === 'away' && (
            <div className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              Possession ▶
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
